import {
  ArrowLeft,
  ArrowRight,
  MousePointerClick,
  Plus,
  Send,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDataProvider, useNotify } from "ra-core";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import type {
  CampaignAnalytics,
  CampaignDef,
  Contact,
  EmailTemplate,
} from "../types";
import { shortDate } from "./assetActivity";
import { useListMembers, useMembersResolver } from "./ListsTab";
import { EmptyState } from "../misc/EmptyState";

/**
 * Bulk email campaigns - a built template sent to a list. Live sending goes
 * through the services EmailSender (Resend/SES with per-tenant SPF/DKIM) at
 * deployment; the preview simulates the send and records the audience size.
 * Opens, clicks, and unsubscribes land on the event spine per recipient.
 */

let uid = Date.now() % 100000;
const nextUid = () => String(++uid);

/**
 * Demo engagement for a simulated send - deterministic per contact id so the
 * numbers hold steady across reloads. Live sends record the same shape from
 * real pixel opens and wrapped-link clicks on the event spine.
 */
const simulateAnalytics = (
  members: Contact[],
  template: EmailTemplate | undefined,
): CampaignAnalytics => {
  const links = (template?.blocks ?? [])
    .filter((b) => b.type === "button" && b.url)
    .map((b) => b.url!)
    .slice(0, 5);
  const linkUrls = links.length > 0 ? links : ["https://example.com/learn-more"];

  const engagement = members.map((m) => {
    const seed = Number(m.id) || 0;
    const opened = seed % 10 < 4; // ~40% open rate
    const opens = opened ? 1 + ((seed * 7) % 3) : 0;
    const clicked = opened && (seed * 13) % 10 < 3; // ~30% CTOR
    const clickedLinks = clicked
      ? [linkUrls[(seed * 3) % linkUrls.length]!]
      : [];
    return {
      contactId: m.id,
      name: `${m.first_name} ${m.last_name}`,
      opened,
      opens,
      clickedLinks,
    };
  });

  const linkCounts = linkUrls.map((url) => ({
    url,
    clicks: engagement.filter((e) => e.clickedLinks.includes(url)).length,
  }));

  return {
    delivered: members.length,
    uniqueOpens: engagement.filter((e) => e.opened).length,
    totalOpens: engagement.reduce((s, e) => s + e.opens, 0),
    uniqueClicks: engagement.filter((e) => e.clickedLinks.length > 0).length,
    links: linkCounts,
    unsubscribes: Math.floor(members.length / 80),
    engagement,
  };
};

const pct = (n: number, of: number) =>
  of === 0 ? "0%" : `${Math.round((n / of) * 100)}%`;

const StatTile = ({ label, value, detail }: { label: string; value: string; detail?: string }) => (
  <div className="rounded-md border border-border px-3 py-2">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-xl font-semibold tabular-nums">{value}</p>
    {detail && <p className="text-[11px] text-muted-foreground tabular-nums">{detail}</p>}
  </div>
);

export const CampaignsTab = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const campaigns = config.campaigns ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const campaign = campaigns.find((c) => c.id === selectedId) ?? null;
  const list =
    (config.contactLists ?? []).find((l) => l.id === campaign?.listId) ?? null;
  const members = useListMembers(list);
  const addressSet = (config.mailingAddress ?? "").trim().length > 0;

  // For the list view: resolve each campaign's analytics (stored at send
  // time, or simulated for the seeded sample send).
  const membersOf = useMembersResolver();
  const analyticsFor = (c: CampaignDef): CampaignAnalytics | null => {
    if (c.status !== "sent") return null;
    if (c.analytics) return c.analytics;
    return simulateAnalytics(
      membersOf((config.contactLists ?? []).find((l) => l.id === c.listId)),
      (config.emailTemplates ?? []).find((t) => t.id === c.templateId),
    );
  };

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row fires immediately for discrete actions (create/
  // delete a campaign, pick a template/list, send) and on blur for the
  // free-typed campaign name, so keystrokes don't spam the network.
  // Without this, campaigns only ever lived in the browser's local store -
  // never actually reaching the database.
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify("Couldn't save that - you may need admin access to update campaigns.", {
        type: "error",
      });
    }
  };

  const saveCampaigns = (next: CampaignDef[], opts: { persistNow?: boolean } = {}) => {
    const nextConfig = { ...config, campaigns: next };
    updateConfig(nextConfig);
    if (opts.persistNow !== false) void persist(nextConfig);
  };
  const flushCampaigns = () => void persist({ ...config, campaigns });
  const patchCampaign = (patch: Partial<CampaignDef>, opts?: { persistNow?: boolean }) => {
    if (!campaign) return;
    saveCampaigns(
      campaigns.map((c) => (c.id === campaign.id ? { ...c, ...patch } : c)),
      opts,
    );
  };
  const createCampaign = () => {
    const id = `cmp-${nextUid()}`;
    saveCampaigns([...campaigns, { id, name: "New campaign", status: "draft" }]);
    setSelectedId(id);
  };

  const ready =
    campaign &&
    campaign.status === "draft" &&
    campaign.templateId &&
    campaign.listId &&
    members.length > 0 &&
    addressSet;

  // ------------------------------------------------------ list view ----
  if (!campaign) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground flex-1">
            Every send with its delivery, opens, and clicks - click a campaign
            for the full breakdown.
          </p>
          <Button variant="outline" size="sm" onClick={createCampaign}>
            <Plus className="w-4 h-4" /> New campaign
          </Button>
        </div>
        {campaigns.length === 0 ? (
          <EmptyState
            icon={Send}
            title="One send, full analytics"
            body="A campaign sends one of your email templates to one of your lists - CAN-SPAM footer attached automatically - and reports delivery, opens, clicks, and exactly who engaged, so the next send is smarter."
            primaryLabel="Create a campaign"
            onPrimary={createCampaign}
            footnote="Live sends go through your deployed workspace with per-tenant SPF/DKIM; the preview simulates."
          />
        ) : (
          <Card>
            <CardContent className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3 font-medium">Campaign</th>
                    <th className="py-1.5 pr-3 font-medium">Status</th>
                    <th className="py-1.5 pr-3 font-medium">Audience</th>
                    <th className="py-1.5 pr-3 font-medium">Delivered</th>
                    <th className="py-1.5 pr-3 font-medium">Open rate</th>
                    <th className="py-1.5 pr-3 font-medium">Click rate</th>
                    <th className="py-1.5 pr-3 font-medium">Unsubs</th>
                    <th className="py-1.5 pr-3 font-medium">Sent</th>
                    <th className="py-1.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const a = analyticsFor(c);
                    const audience = (config.contactLists ?? []).find(
                      (l) => l.id === c.listId,
                    );
                    return (
                      <tr
                        key={c.id}
                        className="border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40"
                        onClick={() => setSelectedId(c.id)}
                      >
                        <td className="py-2 pr-3 font-medium">{c.name}</td>
                        <td className="py-2 pr-3">
                          <span
                            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                              c.status === "sent"
                                ? "bg-[#8fb586] text-white"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {c.status}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {audience?.name ?? "-"}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {a ? a.delivered : "-"}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {a ? pct(a.uniqueOpens, a.delivered) : "-"}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {a ? pct(a.uniqueClicks, a.delivered) : "-"}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {a ? a.unsubscribes : "-"}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                          {shortDate(c.sentAt)}
                        </td>
                        <td className="py-2 text-right">
                          <ArrowRight className="w-3.5 h-3.5 inline text-muted-foreground" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-2">
                Numbers are simulated in the preview - live sends record real
                pixel opens and wrapped-link clicks.{" "}
                <Link
                  to="/help/email-templates-and-campaigns"
                  className="underline hover:no-underline"
                >
                  How campaigns work
                </Link>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ---------------------------------------------------- detail view ----
  const analytics = analyticsFor(campaign);

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setSelectedId(null)}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground self-start"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> All campaigns
      </button>

      <Card>
        <CardContent className="p-4 flex flex-col gap-3 max-w-2xl">
          <div className="flex items-center gap-2">
            <Input
              value={campaign.name}
              onChange={(e) => patchCampaign({ name: e.target.value }, { persistNow: false })}
              onBlur={flushCampaigns}
              className="font-medium"
              disabled={campaign.status === "sent"}
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label="Delete campaign"
              onClick={() => {
                saveCampaigns(campaigns.filter((c) => c.id !== campaign.id));
                setSelectedId(null);
              }}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {campaign.status === "sent" ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm">
                Sent to{" "}
                <span className="font-medium tabular-nums">
                  {campaign.recipients ?? analytics?.delivered ?? 0}
                </span>{" "}
                recipients on{" "}
                {campaign.sentAt ? new Date(campaign.sentAt).toLocaleString() : "-"}.
                <span className="text-muted-foreground">
                  {" "}
                  Opens track via pixel, clicks via wrapped links; numbers
                  below are simulated in the preview.
                </span>
              </p>
              {analytics ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <StatTile
                      label="Delivered"
                      value={String(analytics.delivered)}
                    />
                    <StatTile
                      label="Open rate"
                      value={pct(analytics.uniqueOpens, analytics.delivered)}
                      detail={`${analytics.uniqueOpens} unique · ${analytics.totalOpens} total`}
                    />
                    <StatTile
                      label="Click rate"
                      value={pct(analytics.uniqueClicks, analytics.delivered)}
                      detail={`${analytics.uniqueClicks} clickers`}
                    />
                    <StatTile
                      label="Unsubscribed"
                      value={String(analytics.unsubscribes)}
                    />
                  </div>

                  {analytics.links.length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                        Link performance
                      </p>
                      {analytics.links.map((l) => (
                        <div
                          key={l.url}
                          className="flex items-center gap-2 text-sm py-1 border-t border-border"
                        >
                          <MousePointerClick className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span className="truncate flex-1">{l.url}</span>
                          <span className="tabular-nums text-muted-foreground">
                            {l.clicks} click{l.clicks === 1 ? "" : "s"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                      Who engaged
                    </p>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-card text-left text-muted-foreground">
                          <tr>
                            <th className="py-1 pr-2 font-medium">Contact</th>
                            <th className="py-1 pr-2 font-medium">Opens</th>
                            <th className="py-1 font-medium">Clicked</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.engagement
                            .filter((e) => e.opened)
                            .sort(
                              (a, b) =>
                                b.clickedLinks.length - a.clickedLinks.length ||
                                b.opens - a.opens,
                            )
                            .map((e) => (
                              <tr key={String(e.contactId)} className="border-t border-border">
                                <td className="py-1 pr-2">
                                  <Link
                                    to={`/contacts/${e.contactId}/show`}
                                    className="underline hover:no-underline"
                                  >
                                    {e.name}
                                  </Link>
                                </td>
                                <td className="py-1 pr-2 tabular-nums">{e.opens}</td>
                                <td className="py-1 text-xs text-muted-foreground truncate max-w-48">
                                  {e.clickedLinks.join(", ") || "-"}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No engagement recorded for this send.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-muted-foreground">Send template</span>
                <Select
                  value={campaign.templateId ?? ""}
                  onValueChange={(templateId) => patchCampaign({ templateId })}
                >
                  <SelectTrigger className="w-52">
                    <SelectValue placeholder="pick a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config.emailTemplates ?? []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground">to list</span>
                <Select
                  value={campaign.listId ?? ""}
                  onValueChange={(listId) => patchCampaign({ listId })}
                >
                  <SelectTrigger className="w-52">
                    <SelectValue placeholder="pick a list…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(config.contactLists ?? []).map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {campaign.listId && (
                <p className="text-sm text-muted-foreground tabular-nums">
                  {members.length} recipient{members.length === 1 ? "" : "s"} in
                  this audience.
                </p>
              )}
              {!addressSet && (
                <p className="text-xs text-destructive">
                  Set the physical mailing address on the Emails tab first -
                  campaigns can't send without the CAN-SPAM footer.
                </p>
              )}
              <Button
                className="w-fit"
                disabled={!ready}
                onClick={() =>
                  patchCampaign({
                    status: "sent",
                    sentAt: new Date().toISOString(),
                    recipients: members.length,
                    analytics: simulateAnalytics(
                      members,
                      (config.emailTemplates ?? []).find(
                        (t) => t.id === campaign.templateId,
                      ),
                    ),
                  })
                }
              >
                <Send className="w-4 h-4" /> Send campaign
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
